import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, SlidersHorizontal, ChevronRight, ChevronLeft, Check } from './Icon';

export interface FilterOption {
  value: string;
  label: string;
  count: number;
  /** Shown as a dot before the label — categories carry one, days don't. */
  color?: string;
}

export interface FilterDimension {
  key: string;
  label: string;
  options: FilterOption[];
  /** The value currently applied on this dimension, if any. */
  active: string | null;
}

/**
 * Two-level filter menu: pick a dimension, then a value. Adding a filter later
 * is one more entry in `dimensions` — no layout grows, which is the whole point
 * of this over a row of pills per dimension.
 *
 * The open/outside-click/drop-up handling mirrors `CurrencySelect`, the only
 * other hand-rolled popover in the app.
 */
export function FilterMenu({ dimensions, onPick }: {
  dimensions: FilterDimension[];
  onPick: (dimensionKey: string, value: string | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen]     = useState(false);
  const [dimKey, setDimKey] = useState<string | null>(null);
  const [dropUp, setDropUp] = useState(false);
  const ref    = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - rect.bottom < 320);
    }
    setOpen(o => !o);
    setDimKey(null);
  }

  const dimension = dimensions.find(d => d.key === dimKey) ?? null;
  const usable = dimensions.filter(d => d.options.length > 0);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border font-mono text-[10px] font-semibold tracking-wide transition-colors cursor-pointer ${
          open
            ? 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800'
            : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        <Icon as={SlidersHorizontal} size={12} />
        {t('expenses.filters.button')}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute left-0 z-30 w-56 max-h-72 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1 ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {!dimension && usable.map(d => (
            <button
              key={d.key}
              type="button"
              role="menuitem"
              onClick={() => setDimKey(d.key)}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <span>{d.label}</span>
              <span className="flex items-center gap-1 text-slate-400">
                {d.active && <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />}
                <Icon as={ChevronRight} size={13} />
              </span>
            </button>
          ))}

          {dimension && (
            <>
              <button
                type="button"
                onClick={() => setDimKey(null)}
                className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left font-mono text-[10px] uppercase tracking-wide text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <Icon as={ChevronLeft} size={12} /> {dimension.label}
              </button>
              {dimension.options.map(o => {
                const active = dimension.active === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => {
                      onPick(dimension.key, active ? null : o.value);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    {o.color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: o.color }} />}
                    <span className="flex-1 truncate">{o.label}</span>
                    <span className="font-mono text-[10px] text-slate-400 tabular-nums">{o.count}</span>
                    {active && <Icon as={Check} size={12} className="text-teal-500 shrink-0" />}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
